import HowItWorks from "./HowItWorks/HowItWorks";
import SplitSearchForm from "./SplitSearchForm/SplitSearchForm";
import { useCallback, useEffect, useState } from "react";
import debounce from "lodash/debounce";
import { searchDevices, SearchResponse } from "../../../client/deviceSearch";
import { useTradeInContext } from "../../../TradeInContext";
import {
  Device,
  TRADE_IN_SEARCH_ERROR,
  TRADE_IN_UNAVAILABLE_WARNING,
} from "./types";
import { IMEI_TAB } from "./SplitSearchForm/SearchTabs";
import { dispatchTradeInSearchSelect } from "./analytics";
import DeviceDetails from "./DeviceDetails/DeviceDetails";
import { TRADE_IN_STEPS } from "../TradeInModal";
import { sendEvent } from "@JohnLewisPartnership/experiments-utils";
import { TradeInEvents } from "../../../TradeIn";
import NextStep, { TradeInSteps } from "../NextStep/NextStep";
import styles from "./DeviceSearch.module.scss";

const IMEI_LENGTH = 15;
const SEARCH_DELAY = 500;
const MODEL_MIN_CHARS = 3;
const FAILED_ERROR_ATTEMPTS = 1;
const TIMEOUT = "timeout";

export type Props = {
  searchResults?: Device[];
};

const truncateOriginalString = (term: string) => {
  const strippedTerm = term.replace(/\s/g, "").slice(0, IMEI_LENGTH);
  let origIndex = 0;

  for (const char of strippedTerm) {
    let foundChar = false;

    while (!foundChar) {
      if (term[origIndex] && char === term[origIndex]) foundChar = true;

      origIndex = origIndex + 1;
    }
  }

  return term.slice(0, origIndex);
};

const maskIfIMEI = (term: string) => {
  const termWithoutSpaces = term.split(" ").join("");
  if (termWithoutSpaces.length <= 15 && termWithoutSpaces.length > 9) {
    return /^\d+$/.test(termWithoutSpaces) ? "********" : term;
  }
  return term;
};

const DeviceSearch = ({ searchResults }: Props) => {
  const {
    tradeInBaseUrl,
    onApiError,
    setSelectedDevice,
    errorMessage,
    setErrorMessage,
    setCurrentScreen,
    selectedDevice,
    setDeviceIdentifier,
  } = useTradeInContext();

  const [searchResult, setSearchResult] = useState<Array<Device>>(
    searchResults || [],
  );

  const [searchString, setSearchString] = useState("");
  const [shouldSearch, setShouldSearch] = useState(true);
  const [errorCount, setErrorCount] = useState(0);
  const [abortController, setAbortController] = useState<AbortController>();
  const [isImeiSearch, setIsImeiSearch] = useState(false);

  const formatInputValue = (e: { target: { value: string } }) => {
    const {
      target: { value },
    } = e;

    if (isImeiSearch) {
      const searchTerm = value.replace(/\s/g, "");
      if (searchTerm.length <= IMEI_LENGTH) {
        return value;
      }
      // truncate something that has been pasted:
      else if (value.length > IMEI_LENGTH) {
        return truncateOriginalString(value);
      }
    }

    return value;
  };

  const onInputChange = (e: { target: { value: string } }) => {
    const formattedSearchString = formatInputValue(e);
    setSearchString(formattedSearchString);
    setShouldSearch(true);
    abortController?.abort();
    setSelectedDevice(null);
    setDeviceIdentifier("");

    // clear results & errors if search term has changed
    if (searchString !== formattedSearchString) {
      setSearchResult([]);
      setErrorMessage(null);
    }
  };

  const handleSearch = useCallback(async (searchTerm: string) => {
    const abort = new AbortController();
    setAbortController(abort);

    const timeoutId = setTimeout(() => {
      abort.abort(TIMEOUT);
    }, 3000);

    const response: SearchResponse = await searchDevices(
      tradeInBaseUrl,
      searchTerm,
      abort,
    );

    clearTimeout(timeoutId);

    if (!response || response.status < 200 || response.status > 299) {
      if (abort.signal.aborted === false || abort.signal.reason === TIMEOUT) {
        setErrorCount((errCount) => errCount + 1);
      }
      return;
    }

    if (!response.searchDevices?.results.length) {
      setSearchResult([]);
      setErrorMessage(TRADE_IN_SEARCH_ERROR);
      return;
    }

    setErrorCount(0);
    setErrorMessage(null);
    setSearchResult(response.searchDevices.results);
  }, []);

  const search = useCallback(
    debounce((searchString) => handleSearch(searchString), SEARCH_DELAY),
    [handleSearch],
  );

  const onTabChange = (tabIndex: number) => {
    setIsImeiSearch(tabIndex === IMEI_TAB);
    setSearchString("");
    setSearchResult([]);
    setErrorMessage(null);
    setSelectedDevice(null);
    setDeviceIdentifier("");
  };

  const validModelSearch = (searchTerm: string) =>
    searchTerm !== "" && searchTerm.length >= MODEL_MIN_CHARS;

  const validImeiSearch = (searchTerm: string) =>
    searchTerm !== "" && searchTerm.length === IMEI_LENGTH;

  useEffect(() => {
    if (!shouldSearch) return;

    if (isImeiSearch) {
      const searchTerm = searchString.replace(/\s/g, "");
      if (validImeiSearch(searchTerm)) {
        search(searchTerm);
      }
    } else {
      const searchTerm = searchString.trim().toLocaleLowerCase();
      if (validModelSearch(searchTerm)) {
        search(searchTerm);
      }
    }
  }, [search, searchString, shouldSearch, isImeiSearch]);

  useEffect(() => {
    if (errorCount >= FAILED_ERROR_ATTEMPTS) {
      setErrorMessage(TRADE_IN_UNAVAILABLE_WARNING);
      setSearchResult([]);
    }
  }, [errorCount]);

  const handleSelectDevice = (deviceId: string) => {
    const device = searchResult.find((o) => o.deviceId === deviceId);

    if (device) {
      dispatchTradeInSearchSelect(
        maskIfIMEI(searchString),
        device?.description,
      );
      if (isImeiSearch) {
        setDeviceIdentifier(searchString);
      }
      !isImeiSearch && setSearchString(device.description);
      setSelectedDevice(device);
      setSearchResult([]);
      setShouldSearch(false);
    }
  };

  const handleTradeInDevice = () => {
    sendEvent(TradeInEvents.PW_TRADE_IN_FIND_YOUR_DEVICE_STEP_CTA);
    setCurrentScreen(TRADE_IN_STEPS.GRADING_QUESTIONS);
  };

  useEffect(() => {
    if (errorMessage?.isTechnicalError) {
      onApiError(errorMessage);
    }
  }, [errorMessage, onApiError]);

  return (
    <>
      <SplitSearchForm
        onInputChange={onInputChange}
        searchString={searchString}
        searchResult={searchResult}
        totalResults={searchResults?.length ?? 0}
        onSelectDevice={handleSelectDevice}
        errorMessage={errorMessage}
        onTabChange={onTabChange}
      />
      {selectedDevice && (
        <>
          <DeviceDetails
            onTradeInDevice={handleTradeInDevice}
            {...selectedDevice}
          />
          <div className={styles.findDevice__footer}>
            <NextStep step={TradeInSteps.FIND_DEVICE} />
          </div>
        </>
      )}
      <HowItWorks />
    </>
  );
};

export default DeviceSearch;
